import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import classes from "./RevenueByMonth.module.scss";
import { yearsList } from "../../../../../utils/constants/periodLists";
import { Legend } from "../../../../common/ChartLegend/ChartLegend";
import { PeriodDropdown } from "../../../../common/PeriodDropdown/PeriodDropdown";
import { getYearlyRevenueByMonthData } from "../../../../../redux/selectors/revenueSelector";
import {
  findMaxNumericValue,
  getCurrentYearString,
  getPreviousYearString,
} from "../../../../../utils/commonUtils";
import { isMobileModeSelector } from "../../../../../redux/selectors/appStatusSelector";
import { IconButton } from "@mui/material";
import { BarIcon } from "../../../../../assets/icons/BarIcon";
import { LineIcon } from "../../../../../assets/icons/LineIcon";
import { RevenueByMonthBarChart } from "./components/RevenueByMonthBarChart/RevenueByMonthBarChart";
import { RevenueByMonthLineChart } from "./components/RevenueByMonthLineChart/RevenueByMonthLineChart";
import { Typography } from "../../../../../shared/ui/typography";
import clsx from "clsx";
import { getDisplayDataForRevenueMetrics } from "@/utils/mapDataForRevenueMetrics";

export const RevenueByMonth = () => {
  const [fromYear, setFromYear] = useState(() => getPreviousYearString());
  const [toYear, setToYear] = useState(() => getCurrentYearString());
  const [chartType, setChartType] = useState<"bar" | "line">("line");
  const [maxRevenueValue, setMaxRevenueValue] = useState(0);
  const isBarActive = chartType === "bar";

  const isMobileView = useSelector(isMobileModeSelector);
  const data = useSelector(getYearlyRevenueByMonthData);

  const years = yearsList();

  const revenueBarLegend = [
    {
      label: fromYear,
      color: "#00B48D",
      legendType: "circle",
    },
    {
      label: toYear,
      color: "#5D6293",
      legendType: "circle",
    },
  ];

  const displayData = useMemo(
    () =>
      data &&
      getDisplayDataForRevenueMetrics({
        data,
        toYear,
        fromYear,
      }),
    [data, fromYear, toYear]
  );

  useEffect(() => {
    if (displayData) {
      setMaxRevenueValue(findMaxNumericValue(displayData));
    }
  }, [displayData]);

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className={clsx(classes.wrapper, classes.errorMessageContainer)}>
        <Typography
          variant="text-lg"
          weight="medium"
          className={classes.noDataMessage}
        >
          No data available for the selected period.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.revenueBarContainer}>
        <div className={classes.revenueBarHeader}>
          <div className={classes.revenueBarHeaderLeft}>
            <Typography
              className={classes.gray900}
              variant="text-xl"
              weight="semibold"
            >
              Revenue
            </Typography>
            {isMobileView && (
              <div className={classes.switcher}>
                <IconButton
                  classes={{ root: classes.button }}
                  className={isBarActive ? classes.activeButton : ""}
                  color={isBarActive ? "secondary" : "inherit"}
                  aria-label="change chart type"
                  onClick={() => setChartType("bar")}
                >
                  <BarIcon />
                </IconButton>
                <IconButton
                  classes={{ root: classes.button }}
                  className={!isBarActive ? classes.activeButton : ""}
                  color={!isBarActive ? "secondary" : "inherit"}
                  aria-label="change chart type"
                  onClick={() => setChartType("line")}
                >
                  <LineIcon />
                </IconButton>
              </div>
            )}
          </div>

          {!isMobileView && (
            <div className={classes.switcher}>
              {!isMobileView && (
                <div className={classes.periodBlock}>
                  <>
                    <PeriodDropdown
                      period={fromYear}
                      periodList={years}
                      setPeriod={setFromYear}
                      variant="standard"
                    />
                    <Typography
                      variant="text-md"
                      weight="medium"
                      className={classes.vs}
                    >
                      vs.
                    </Typography>
                    <PeriodDropdown
                      period={toYear}
                      periodList={years}
                      setPeriod={setToYear}
                      variant="standard"
                    />
                  </>
                </div>
              )}
              <div className={classes.divider}></div>
              <IconButton
                classes={{ root: classes.button }}
                className={isBarActive ? classes.activeButton : ""}
                color={isBarActive ? "secondary" : "inherit"}
                aria-label="change chart type"
                onClick={() => setChartType("bar")}
              >
                <BarIcon />
              </IconButton>
              <IconButton
                classes={{ root: classes.button }}
                className={!isBarActive ? classes.activeButton : ""}
                color={!isBarActive ? "secondary" : "inherit"}
                aria-label="change chart type"
                onClick={() => setChartType("line")}
              >
                <LineIcon />
              </IconButton>
            </div>
          )}
        </div>
        {isMobileView && (
          <div className={classes.periodBlock}>
            <>
              <PeriodDropdown
                period={fromYear}
                periodList={years}
                setPeriod={setFromYear}
                variant="standard"
              />
              <Typography
                variant="text-md"
                weight="medium"
                className={classes.vs}
              >
                vs.
              </Typography>
              <PeriodDropdown
                period={toYear}
                periodList={years}
                setPeriod={setToYear}
                variant="standard"
              />
            </>
          </div>
        )}
        {isBarActive
          ? displayData && (
              <RevenueByMonthBarChart
                data={displayData}
                fromYear={fromYear}
                toYear={toYear}
                maxRevenueValue={maxRevenueValue}
              />
            )
          : displayData && (
              <RevenueByMonthLineChart
                data={displayData}
                fromYear={fromYear}
                toYear={toYear}
                maxRevenueValue={maxRevenueValue}
              />
            )}
        <div className={classes.legendRow}>
          <Legend legendData={revenueBarLegend} />
        </div>
      </div>
    </div>
  );
};
